import { format } from "date-fns";
import { ru, kk } from "date-fns/locale";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { DateRange, DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import "./Calendar.module.css";
import styles from "./Calendar.module.css";
import { useTranslation } from "react-i18next";

export interface ICalendarProps<T> {
  startDate?: Date;
  endDate?: Date;
  setStartDate: Dispatch<SetStateAction<Date | undefined>>;
  setEndDate: Dispatch<SetStateAction<Date | undefined>>;
  /** Показывать выпадающие списки месяца и года в шапке */
  showMonthYearSelect?: boolean;
  /** Подпись над календарём; по умолчанию components.choose_date */
  label?: string;
}

export const Calendar: React.FC<ICalendarProps<any>> = ({
  startDate,
  setStartDate,
  setEndDate,
  endDate,
  showMonthYearSelect = false,
  label,
}) => {
  const { t, i18n } = useTranslation();
  const title = label ?? `${t("components.choose_date")}:`;

  const [range, setRange] = useState<DateRange | undefined>(undefined);
  const current = new Date();
  const initialBase = startDate ?? current;
  const [displayMonth, setDisplayMonth] = useState<Date>(
    new Date(initialBase.getFullYear(), initialBase.getMonth(), 1),
  );

  const months = i18n.language?.includes("ru")
    ? [
        "Январь",
        "Февраль",
        "Март",
        "Апрель",
        "Май",
        "Июнь",
        "Июль",
        "Август",
        "Сентябрь",
        "Октябрь",
        "Ноябрь",
        "Декабрь",
      ]
    : [
        "Қаңтар",
        "Ақпан",
        "Наурыз",
        "Сәуір",
        "Мамыр",
        "Маусым",
        "Шілде",
        "Тамыз",
        "Қыркүйек",
        "Қазан",
        "Қараша",
        "Желтоқсан",
      ];

  const years = (() => {
    const start = 2015;
    const end = current.getFullYear();
    const arr: number[] = [];
    for (let y = end; y >= start; y--) arr.push(y);
    return arr;
  })();

  useEffect(() => {
    setRange({ from: startDate, to: endDate });
  }, [startDate, endDate]);

  useEffect(() => {
    if (!startDate && !endDate) return;
    const base = startDate ?? endDate!;
    setDisplayMonth(new Date(base.getFullYear(), base.getMonth(), 1));
  }, [startDate, endDate]);

  const monthIdx = displayMonth.getMonth();
  const yearVal = displayMonth.getFullYear();

  const isMonthDisabled = (mIdx: number, y: number) => {
    if (y > current.getFullYear()) return true;
    if (y < current.getFullYear()) return false;
    return mIdx > current.getMonth();
  };

  const applyMonthYear = (mIdx: number, y: number) => {
    const candidate = new Date(y, mIdx, 1);
    const nowStart = new Date(current.getFullYear(), current.getMonth(), 1);
    if (candidate > nowStart) return;
    setDisplayMonth(candidate);
  };

  let footer = <p className={styles.label}> </p>;
  if (range?.from) {
    if (!range.to) {
      footer = (
        <p className={styles.label}>{format(range?.from, "dd.MM.yyyy")}</p>
      );
    } else if (range.to) {
      footer = (
        <p className={styles.label}>
          {format(range?.from, "dd.MM.yyyy")}–{format(range?.to, "dd.MM.yyyy")}{" "}
        </p>
      );
    }
  }

  return (
    <div className="my-3">
      <span className={styles.label}>{title}</span>
      {footer}
      {showMonthYearSelect && (
        <div className={styles.monthYearRow}>
          <select
            value={monthIdx}
            onChange={(e) => applyMonthYear(Number(e.target.value), yearVal)}
            className={styles.select}
          >
            {months.map((label, idx) => (
              <option
                key={idx}
                value={idx}
                disabled={isMonthDisabled(idx, yearVal)}
              >
                {label}
              </option>
            ))}
          </select>
          <select
            value={yearVal}
            onChange={(e) => applyMonthYear(monthIdx, Number(e.target.value))}
            className={styles.select}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      )}
      <DayPicker
        locale={i18n.language?.includes("ru") ? ru : kk}
        id="test"
        mode="range"
        month={displayMonth}
        onMonthChange={(m) => {
          if (m) setDisplayMonth(m);
        }}
        selected={range}
        onSelect={(date) => {
          setStartDate(date?.from);
          setEndDate(date?.to);
          setRange(date);
        }}
        className={styles.rdp}
        disabled={{ after: new Date() }}
      />
    </div>
  );
};

export default Calendar;
